import { ChangeEvent, ReactNode, useState } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileArrowUp } from "@fortawesome/pro-solid-svg-icons"
import DeleteIcon from "@mui/icons-material/Delete"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"

import fallbackSvgUrl from "./no-image.png"

type SupportedFiles = "jpg" | "png" | "gif"
type SupportedMimeTypes = Record<SupportedFiles, string>

const supportedFileTypes: SupportedMimeTypes = {
    jpg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
}

const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB in bytes

const useStyles = makeStyles()(() => ({
    noImageBox: {
        display: "block",
        border: " 0.12rem dashed",
        borderRadius: "1rem",
        color: "#287faf",
        height: "18.75rem",
        width: "100%",
    },
    image: {
        width: "100%",
        objectFit: "contain",
    },
    uploadIcon: {
        position: "relative",
        left: "50%",
        top: "35%",
        transform: "translate(-50%, -50%)",
    },
    uploadContents: {
        position: "relative",
        left: "50%",
        top: "40%",
        transform: "translate(-50%, -50%)",
    },
    fileInput: {
        opacity: 0,
        textAlign: "center",
        height: "100%",
        width: "100%",
        cursor: "pointer",
        position: "relative",
        left: "50%",
        top: "15%",
        transform: "translate(-50%, -50%)",
    },
}))

function getAcceptedFiles(fileExts: SupportedMimeTypes) {
    return Object.values(fileExts).join(",")
}

function printSupportedFileTypes(fileExts: SupportedMimeTypes) {
    return Object.keys(fileExts).join(", ").toUpperCase()
}

function formatFileSize(fileSize: number) {
    if (fileSize < 1024) {
        return `${fileSize} bytes`
    } else if (fileSize >= 1024 && fileSize < 1048576) {
        return `${(fileSize / 1024).toFixed(1)} KB`
    }

    return `${(fileSize / 1048576).toFixed(1)} MB`
}

function createImagePreview(image: File, cb: (imageUrl: string) => void) {
    const reader = new FileReader()

    reader.onloadend = () => {
        cb && cb(reader.result?.toString() ?? "")
    }

    reader.readAsDataURL(image)
}

type ImageUploadDropzoneProps = Readonly<{
    previewImage?: string
    onImageChange: (image: File | null) => void
    bottomContent?: ReactNode
}>

export function ImageUploadDropzone({ onImageChange, previewImage, bottomContent }: ImageUploadDropzoneProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(previewImage ?? "")
    const [error, setError] = useState<string>()

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0] : null
        if (!file) return
        setError(undefined)
        if (file.size > MAX_FILE_SIZE) {
            const msg = l10n.getString("image-uploader-max-file-size-exceeded", {
                maxFileSize: formatFileSize(MAX_FILE_SIZE),
            })
            setError(msg)
            setPreviewImageUrl(null)
            onImageChange(null)
            return
        }
        createImagePreview(file, setPreviewImageUrl)
        onImageChange(file)
    }

    return (
        <>
            {!!previewImageUrl ? (
                <>
                    <Box width="100%" textAlign="center">
                        <img
                            src={previewImageUrl}
                            alt="Uploaded Image"
                            onError={() => setPreviewImageUrl(fallbackSvgUrl)}
                            className={classes.image}
                        />
                    </Box>
                    <Stack justifyContent="center" paddingTop=".25rem" direction="row" spacing={2}>
                        <Button
                            disableElevation
                            size="small"
                            variant="outlined"
                            color="primary"
                            component="label"
                            endIcon={<FontAwesomeIcon icon={faFileArrowUp} />}
                        >
                            {l10n.getString("image-previewer-button-label")}
                            <input
                                id="upload-image-hidden"
                                accept={getAcceptedFiles(supportedFileTypes)}
                                type="file"
                                hidden
                                onChange={handleImageChange}
                            />
                        </Button>

                        <Button
                            disableElevation
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => {
                                setPreviewImageUrl(null)
                                onImageChange(null)
                            }}
                            endIcon={<DeleteIcon />}
                        >
                            {l10n.getString("image-previewer-button-delete")}
                        </Button>
                    </Stack>
                </>
            ) : (
                <Box component="label" className={classes.noImageBox}>
                    <FontAwesomeIcon icon={faFileArrowUp} size="3x" className={classes.uploadIcon} />

                    <Box className={classes.uploadContents}>
                        <LocalizedStrict id="image-uploader-drag-and-drop">
                            <Typography variant="subtitle1" gutterBottom align="center">
                                Drag and Drop Browse
                            </Typography>
                        </LocalizedStrict>

                        <LocalizedStrict
                            id="create-image-file-support"
                            vars={{
                                supportedFileTypes: printSupportedFileTypes(supportedFileTypes),
                                maxSize: formatFileSize(MAX_FILE_SIZE),
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                flexWrap={"wrap"}
                                gutterBottom
                            >
                                {`File types supported: ${printSupportedFileTypes(
                                    supportedFileTypes,
                                )}. Max size: ${formatFileSize(MAX_FILE_SIZE)}`}
                            </Typography>
                        </LocalizedStrict>
                        {error !== undefined && (
                            <Typography variant="body2" color="error" align="center" flexWrap={"wrap"}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                    <input
                        id="upload-image"
                        accept={getAcceptedFiles(supportedFileTypes)}
                        type="file"
                        onChange={handleImageChange}
                        className={classes.fileInput}
                    />
                </Box>
            )}
            {bottomContent ?? <></>}
        </>
    )
}

